import { useState, useRef, useCallback } from 'react';
import {
  Upload, Sparkles, Trash2, Loader2, Check, AlertCircle,
  CheckSquare, Square, ImageOff, Image, Image as ImageIcon,
} from 'lucide-react';
import adminAxios from '../lib/adminAxios';
import useProducts from '../hooks/useProducts';
import useProductImages, { invalidateProductImages } from '../hooks/useProductImages';
import { codeToSlug } from '../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Derive status from manifest entry
const getStatus = (imgs) => {
  if (!imgs || imgs.length === 0) return 'none';
  if (imgs.some((u) => u.includes('-clean'))) return 'processed';
  return 'raw';
};

const STATUS_LABELS = { none: 'No photo', raw: 'Raw photo', processed: 'Processed' };
const STATUS_COLORS = {
  none:      'text-mb-muted/50',
  raw:       'text-gold/70',
  processed: 'text-gold',
};

export default function MediaLibraryPanel() {
  const { products: ALL_PRODUCTS } = useProducts();
  const { manifest, refetch } = useProductImages();
  const allProducts = Object.values(ALL_PRODUCTS).sort((a, b) => a.code.localeCompare(b.code));

  // ── Filter ────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState('all');

  const filtered = allProducts.filter((p) => {
    const s = getStatus(manifest[p.code]);
    if (filter === 'none')      return s === 'none';
    if (filter === 'raw')       return s === 'raw';
    if (filter === 'processed') return s === 'processed';
    return true;
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = { none: 0, raw: 0, processed: 0 };
  allProducts.forEach((p) => stats[getStatus(manifest[p.code])]++);

  // ── Multi-select ──────────────────────────────────────────────────────────
  const [selected, setSelected] = useState(new Set());
  const toggleSelect = (code) =>
    setSelected((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const clearSelect = () => setSelected(new Set());

  // ── Per-card state ────────────────────────────────────────────────────────
  const [cardBusy, setCardBusy] = useState({});   // {code: 'upload'|'beautify'|'delete'}
  const [cardResult, setCardResult] = useState({}); // {code: {ok, msg}}
  const [bust, setBust] = useState(Date.now());

  const done = async (code, result) => {
    setCardResult((r) => ({ ...r, [code]: result }));
    await refetch();
    invalidateProductImages();
    setBust(Date.now());
    setTimeout(() => setCardResult((r) => { const n = { ...r }; delete n[code]; return n; }), 4000);
  };

  // ── Per-card upload (file picker) ─────────────────────────────────────────
  const fileInputs = useRef({});
  const uploadForCode = useCallback(async (code, file) => {
    setCardBusy((b) => ({ ...b, [code]: 'upload' }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      await adminAxios.post(`${API}/admin/product-images/${encodeURIComponent(code)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await done(code, { ok: true, msg: 'Photo uploaded.' });
    } catch (e) {
      await done(code, { ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setCardBusy((b) => { const n = { ...b }; delete n[code]; return n; });
    }
  }, []);

  // ── Beautify single ───────────────────────────────────────────────────────
  const beautify = useCallback(async (code) => {
    setCardBusy((b) => ({ ...b, [code]: 'beautify' }));
    try {
      await adminAxios.post(`${API}/admin/generate-product-image`, {
        code, variant: `clean-${Date.now().toString(36).slice(-4)}`,
      });
      await done(code, { ok: true, msg: 'INK image generated.' });
    } catch (e) {
      await done(code, { ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setCardBusy((b) => { const n = { ...b }; delete n[code]; return n; });
    }
  }, []);

  // ── Delete all images for a product ──────────────────────────────────────
  const deleteImages = useCallback(async (code) => {
    if (!confirm(`Delete all uploaded images for ${code}?`)) return;
    setCardBusy((b) => ({ ...b, [code]: 'delete' }));
    try {
      await adminAxios.delete(`${API}/admin/product-images/${encodeURIComponent(code)}`);
      await done(code, { ok: true, msg: 'Images removed.' });
    } catch (e) {
      await done(code, { ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setCardBusy((b) => { const n = { ...b }; delete n[code]; return n; });
    }
  }, []);

  // ── Batch beautify ────────────────────────────────────────────────────────
  const [batchProgress, setBatchProgress] = useState({}); // {code: 'pending'|'done'|'error'}
  const [batchRunning, setBatchRunning] = useState(false);

  const runBatchBeautify = async () => {
    const codes = [...selected];
    if (!codes.length) return;
    setBatchRunning(true);
    setBatchProgress(Object.fromEntries(codes.map((c) => [c, 'pending'])));
    for (const code of codes) {
      setBatchProgress((p) => ({ ...p, [code]: 'running' }));
      try {
        await adminAxios.post(`${API}/admin/generate-product-image`, {
          code, variant: `clean-${Date.now().toString(36).slice(-4)}`,
        });
        setBatchProgress((p) => ({ ...p, [code]: 'done' }));
      } catch {
        setBatchProgress((p) => ({ ...p, [code]: 'error' }));
      }
    }
    await refetch();
    invalidateProductImages();
    setBust(Date.now());
    setBatchRunning(false);
    clearSelect();
    setTimeout(() => setBatchProgress({}), 5000);
  };

  // ── Global drag-drop bulk upload ──────────────────────────────────────────
  const [isDragOver, setIsDragOver] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const uploadBulk = async (files) => {
    if (!files.length) return;
    setBulkUploading(true);
    setBulkResults(null);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    try {
      const { data } = await adminAxios.post(`${API}/admin/images/bulk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResults(data);
      await refetch();
      invalidateProductImages();
      setBust(Date.now());
    } catch (e) {
      setBulkResults({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBulkUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    uploadBulk(e.dataTransfer.files);
  };

  const dropZoneFileRef = useRef();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-media-library">
      {/* Header */}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
        <div>
          <div className="eyebrow text-gold mb-4">Media Library</div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">All product images.</h2>
          <div className="mt-3 flex gap-5 flex-wrap">
            <span className="eyebrow-sm text-gold">{stats.processed} processed</span>
            <span className="eyebrow-sm text-gold/60">{stats.raw} raw</span>
            <span className="eyebrow-sm text-mb-muted">{stats.none} no photo</span>
          </div>
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-4">
            <span className="eyebrow-sm text-mb-muted">{selected.size} selected</span>
            <button
              className="btn-gold disabled:opacity-50"
              onClick={runBatchBeautify}
              disabled={batchRunning}
              data-testid="admin-batch-beautify"
            >
              {batchRunning
                ? <><Loader2 size={14} className="animate-spin inline mr-1" />Running…</>
                : <><Sparkles size={14} strokeWidth={1.25} className="inline mr-1" />Beautify {selected.size}</>}
            </button>
            <button className="eyebrow-sm text-mb-muted hover:text-gold" onClick={clearSelect}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Drag-drop upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
        className={`mb-8 hairline transition-colors cursor-pointer
          ${isDragOver ? 'border-gold bg-mb-surface' : 'border-subtle hover:border-gold/50'}`}
        onClick={() => dropZoneFileRef.current?.click()}
        data-testid="admin-drop-zone"
      >
        <input
          ref={dropZoneFileRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadBulk(e.target.files)}
          data-testid="admin-bulk-file-input"
        />
        <div className="py-10 flex flex-col items-center gap-3 pointer-events-none">
          {bulkUploading
            ? <><Loader2 size={24} strokeWidth={1} className="text-gold animate-spin" />
                <span className="eyebrow text-gold">Uploading…</span></>
            : <><Upload size={24} strokeWidth={1} className={isDragOver ? 'text-gold' : 'text-mb-muted'} />
                <span className={`eyebrow ${isDragOver ? 'text-gold' : 'text-mb-muted'}`}>
                  Drop any number of product photos here, or click to browse
                </span>
                <span className="eyebrow-sm text-mb-muted/60">
                  Name files <code className="text-gold/70">gv-4439.jpg</code> to auto-match to a product
                </span></>}
        </div>
      </div>

      {/* Bulk upload results */}
      {bulkResults && (
        <div className="mb-8 hairline border-subtle p-5 bg-mb-surface" data-testid="admin-bulk-results">
          {bulkResults.error
            ? <div className="text-mb-muted eyebrow-sm">{bulkResults.error}</div>
            : <div className="space-y-1">
                <div className="eyebrow text-gold mb-3">
                  {bulkResults.stored} uploaded · {bulkResults.failed} failed
                </div>
                {bulkResults.results?.map((r) => (
                  <div key={r.filename} className={`flex gap-3 items-start text-[12px] ${r.ok ? 'text-mb-muted' : 'text-mb-muted/50'}`}>
                    {r.ok ? <Check size={12} className="mt-0.5 text-gold shrink-0" /> : <AlertCircle size={12} className="mt-0.5 shrink-0" />}
                    <span>{r.filename}</span>
                    {r.ok ? <span className="text-gold">→ {r.code}</span> : <span>{r.error}</span>}
                  </div>
                ))}
              </div>}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-6 mb-8 hairline-b border-subtle pb-6 flex-wrap">
        {[
          { id: 'all',       label: `All (${allProducts.length})` },
          { id: 'none',      label: `No photo (${stats.none})` },
          { id: 'raw',       label: `Raw (${stats.raw})` },
          { id: 'processed', label: `Processed (${stats.processed})` },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`eyebrow-sm transition-colors ${filter === id ? 'text-gold border-b border-gold pb-1' : 'text-mb-muted hover:text-gold'}`}
            data-testid={`admin-filter-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Product grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((p) => {
          const imgs = manifest[p.code] || [];
          const cleanImgs = imgs.filter((u) => u.includes('-clean'));
          const status = getStatus(imgs);
          const isSelected = selected.has(p.code);
          const bpState = batchProgress[p.code];
          const busy = cardBusy[p.code];
          const result = cardResult[p.code];
          const mainSrc = cleanImgs[0] || imgs[0] || null;

          return (
            <div
              key={p.code}
              data-testid={`media-card-${codeToSlug(p.code)}`}
              className={`bg-mb-surface hairline p-4 transition-colors
                ${isSelected ? 'border-gold' : 'border-subtle'}`}
            >
              {/* Checkbox + image */}
              <div className="relative">
                <button
                  className="absolute top-2 left-2 z-10 p-1 bg-mb/80 backdrop-blur-sm"
                  onClick={() => toggleSelect(p.code)}
                  data-testid={`media-select-${codeToSlug(p.code)}`}
                  title="Select for batch beautify"
                >
                  {isSelected
                    ? <CheckSquare size={16} strokeWidth={1.25} className="text-gold" />
                    : <Square size={16} strokeWidth={1.25} className="text-mb-muted" />}
                </button>
                <div className="aspect-[4/5] bg-mb hairline border-subtle overflow-hidden">
                  {mainSrc
                    ? <img
                        key={`${p.code}-${bust}`}
                        src={`${mainSrc}?t=${bust}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    : <div className="w-full h-full flex items-center justify-center">
                        <ImageOff size={24} strokeWidth={1} className="text-mb-muted/40" />
                      </div>}
                  {bpState && (
                    <div className="absolute inset-0 bg-mb/70 flex items-center justify-center">
                      {bpState === 'running' && <Loader2 size={20} className="animate-spin text-gold" />}
                      {bpState === 'done'    && <Check size={20} className="text-gold" />}
                      {bpState === 'error'   && <AlertCircle size={20} className="text-mb-muted" />}
                    </div>
                  )}
                </div>
              </div>

              {/* Name + status */}
              <div className="mt-3">
                <div className="eyebrow-sm text-gold">{p.code}</div>
                <div className="mt-1 font-display text-[14px] text-mb leading-tight">{p.name}</div>
                <div className={`mt-1 eyebrow-sm ${STATUS_COLORS[status]}`}>
                  {STATUS_LABELS[status]}
                  {imgs.length > 0 && <span className="ml-2 opacity-50">({imgs.length})</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 flex gap-2">
                {/* Upload */}
                <button
                  className="flex-1 btn-outline-gold text-[11px] py-1.5 disabled:opacity-40"
                  title="Upload photo"
                  disabled={!!busy}
                  onClick={() => fileInputs.current[p.code]?.click()}
                  data-testid={`media-upload-${codeToSlug(p.code)}`}
                >
                  {busy === 'upload'
                    ? <Loader2 size={12} className="animate-spin mx-auto" />
                    : <Upload size={12} strokeWidth={1.25} className="mx-auto" />}
                </button>
                <input
                  ref={(el) => { fileInputs.current[p.code] = el; }}
                  type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadForCode(p.code, f); e.target.value = ''; }}
                />

                {/* Beautify */}
                <button
                  className="flex-1 btn-outline-gold text-[11px] py-1.5 disabled:opacity-40"
                  title="Clean on-brand with AI"
                  disabled={!!busy || imgs.length === 0}
                  onClick={() => beautify(p.code)}
                  data-testid={`media-beautify-${codeToSlug(p.code)}`}
                >
                  {busy === 'beautify'
                    ? <Loader2 size={12} className="animate-spin mx-auto" />
                    : <Sparkles size={12} strokeWidth={1.25} className="mx-auto" />}
                </button>

                {/* Delete */}
                <button
                  className="btn-outline-gold text-[11px] py-1.5 px-2.5 disabled:opacity-40
                    hover:border-red-900/50 hover:text-red-400"
                  title="Delete all images"
                  disabled={!!busy || imgs.length === 0}
                  onClick={() => deleteImages(p.code)}
                  data-testid={`media-delete-${codeToSlug(p.code)}`}
                >
                  {busy === 'delete'
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Trash2 size={12} strokeWidth={1.25} />}
                </button>
              </div>

              {/* Per-card result */}
              {result && (
                <div className={`mt-3 px-3 py-2 hairline eyebrow-sm ${result.ok ? 'border-gold text-gold' : 'border-subtle text-mb-muted'}`}>
                  {result.msg}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
